import { useDeferredValue, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BUCKET_FIELDS, STAGES, type BucketField, type BucketRule, type OrderMatch } from '@joker/domain'
import type { CohortOptions } from '@joker/contracts'
import { useTRPC } from '../trpc'
import { ErrorNote } from '../ui'
import { FIELD_INFO, OPS_FOR, opLabel, type FieldOp } from './cohortFields'
import { TagPicker } from './TagPicker'

/**
 * The cohort editor: a name, a list of conditions joined by AND or OR, and an
 * optional "only people who paid". It writes an ordinary bucket rule, so the
 * worker, the counts and the Cohorts screen treat it like any other bucket.
 *
 * The rule it writes has one fixed shape, so it can be read back for editing:
 *   { all: [ { all|any: [conditions] }, paidCents > 0 ] }   or just the inner group,
 * and a single condition on its own when there is only one. Every starter
 * cohort has that shape too, bar one, which the editor declines rather than
 * rewrite.
 */

type Base = { exclude: boolean }
export type Row =
  | (Base & { kind: 'order'; products: string[]; min: string; max: string; from: string; before: string; code: string })
  | (Base & { kind: 'signupSource' | 'signupPromo' | 'stage'; values: string[] })
  /** Carries any of these hand-set tags (ids). */
  | (Base & { kind: 'tag'; values: string[] })
  | (Base & { kind: 'sessions'; op: 'gte' | 'lte'; n: string })
  /** Any other field, compared any way the field allows. */
  | (Base & { kind: 'field'; field: BucketField; op: FieldOp; value: string })

export interface Draft {
  name: string
  description: string
  join: 'all' | 'any'
  paidOnly: boolean
  rows: Row[]
  /** Which list it opens: patients, or leads who have not bought. */
  audience: 'patients' | 'prospects'
}

const KIND_LABEL: Record<Row['kind'], string> = {
  order: 'Bought',
  signupSource: 'Signed up from',
  signupPromo: 'Sign-up promo',
  stage: 'Stage',
  sessions: 'Training sessions',
  tag: 'Tag',
  field: 'Other',
}

const blank = (kind: Row['kind']): Row =>
  kind === 'order'
    ? { kind, exclude: false, products: [], min: '', max: '', from: '', before: '', code: '' }
    : kind === 'sessions'
      ? { kind, exclude: false, op: 'gte', n: '1' }
      : kind === 'field'
        ? { kind, exclude: false, field: 'isVip', op: 'eq', value: 'true' }
        : { kind, exclude: false, values: [] }

export const emptyDraft = (): Draft => ({
  name: '', description: '', join: 'all', paidOnly: true, rows: [blank('order')], audience: 'patients',
})

const PAID: BucketRule = { field: 'paidCents', op: 'gt', value: 0 }
const dollarsToCents = (s: string) => (s.trim() === '' || isNaN(Number(s)) ? undefined : Math.round(Number(s) * 100))

function rowRule(r: Row): BucketRule | null {
  let base: BucketRule | null = null
  if (r.kind === 'order') {
    const m: OrderMatch = {}
    if (r.products.length) m.products = r.products
    const min = dollarsToCents(r.min)
    const max = dollarsToCents(r.max)
    if (min != null) m.minCents = min
    if (max != null) m.maxCents = max
    if (r.from) m.paidFrom = r.from
    if (r.before) m.paidBefore = r.before
    if (r.code.trim()) m.codes = [r.code.trim()]
    base = { order: m }
  } else if (r.kind === 'sessions') {
    if (r.n.trim() === '' || isNaN(Number(r.n))) return null
    base = { field: 'sessionsTotal', op: r.op, value: Number(r.n) }
  } else if (r.kind === 'field') {
    base = fieldRule(r)
    if (!base) return null
  } else if (r.kind === 'tag') {
    if (!r.values.length) return null
    base = { tags: r.values }
  } else {
    if (!r.values.length) return null
    base = { field: r.kind, op: 'in', value: r.values }
  }
  return r.exclude ? { not: base } : base
}

function fieldRule(r: Extract<Row, { kind: 'field' }>): BucketRule | null {
  const info = FIELD_INFO[r.field]
  if (r.op === 'isNull' || r.op === 'notNull') return { field: r.field, op: r.op }
  if (info.kind === 'boolean') return { field: r.field, op: 'eq', value: r.value === 'true' }
  if (info.kind === 'number') {
    if (r.value.trim() === '' || isNaN(Number(r.value))) return null
    return { field: r.field, op: r.op, value: Number(r.value) * (info.scale ?? 1) }
  }
  if (r.op !== 'eq' && r.op !== 'neq') return null
  return { field: r.field, op: r.op, value: r.value }
}

export function draftRule(d: Draft): BucketRule | null {
  const parts = d.rows.map(rowRule).filter((x): x is BucketRule => x !== null)
  if (!parts.length) return null
  /* One condition stands on its own, as the starter cohorts are written. */
  const group: BucketRule = parts.length === 1 && !d.paidOnly ? parts[0]! : d.join === 'all' ? { all: parts } : { any: parts }
  return d.paidOnly ? { all: [group, PAID] } : group
}

/** Reads a rule this editor wrote back into a draft; null for any other shape. */
export function ruleToDraft(
  rule: unknown,
  name: string,
  description: string | null,
  audience: 'patients' | 'prospects' = 'patients',
): Draft | null {
  let r = rule as BucketRule
  let paidOnly = false
  /* Compared by its parts, not as text: Postgres stores JSON keys in its own order. */
  const isPaid = (x: BucketRule | undefined) =>
    !!x && 'field' in x && x.field === 'paidCents' && x.op === 'gt' && x.value === 0
  if ('all' in r && r.all.length === 2 && isPaid(r.all[1]) && ('all' in r.all[0]! || 'any' in r.all[0]!)) {
    paidOnly = true
    r = r.all[0]!
  }
  const join: 'all' | 'any' = 'any' in r ? 'any' : 'all'
  /* A lone condition is a group of one. */
  const list = 'all' in r ? r.all : 'any' in r ? r.any : [r]
  const rows: Row[] = []
  for (const item of list) {
    const exclude = 'not' in item
    const c = exclude ? (item as { not: BucketRule }).not : item
    if ('tags' in c) {
      rows.push({ kind: 'tag', exclude, values: [...c.tags] })
    } else if ('order' in c) {
      const m = c.order
      rows.push({
        kind: 'order', exclude, products: m.products ?? [],
        min: m.minCents != null ? String(m.minCents / 100) : '', max: m.maxCents != null ? String(m.maxCents / 100) : '',
        from: m.paidFrom ?? '', before: m.paidBefore ?? '', code: m.codes?.[0] ?? '',
      })
    } else if ('field' in c && c.field === 'sessionsTotal' && (c.op === 'gte' || c.op === 'lte')) {
      rows.push({ kind: 'sessions', exclude, op: c.op, n: String(c.value) })
    } else if ('field' in c && (c.field === 'signupSource' || c.field === 'signupPromo' || c.field === 'stage') && c.op === 'in') {
      rows.push({ kind: c.field, exclude, values: c.value.map(String) })
    } else if ('field' in c && (BUCKET_FIELDS as readonly string[]).includes(c.field) && c.op !== 'in' && c.op !== 'nin') {
      const info = FIELD_INFO[c.field]
      const raw = 'value' in c ? c.value : null
      const value = raw === null ? '' : typeof raw === 'number' ? String(raw / (info.scale ?? 1)) : String(raw)
      rows.push({ kind: 'field', exclude, field: c.field, op: c.op as FieldOp, value })
    } else return null
  }
  return { name, description: description ?? '', join, paidOnly, rows, audience }
}

/** The rule in words, with product names instead of catalog ids. */
export function describeDraft(d: Draft, options: CohortOptions | undefined): string {
  const productName = (id: string) => options?.products.find((p) => p.id === id)?.name ?? id
  const parts = d.rows.map((r) => {
    let t: string
    if (r.kind === 'order') {
      const bits = [r.products.length ? r.products.map(productName).join(' or ') : 'anything']
      if (r.min) bits.push(`for $${r.min} or more`)
      if (r.max) bits.push(`for $${r.max} or less`)
      if (r.from) bits.push(`on or after ${r.from}`)
      if (r.before) bits.push(`before ${r.before}`)
      if (r.code) bits.push(`with code ${r.code}`)
      t = `bought ${bits.join(' ')}`
    } else if (r.kind === 'sessions') t = `${r.op === 'gte' ? 'at least' : 'at most'} ${r.n} training sessions`
    else if (r.kind === 'tag') {
      t = `tagged ${r.values.map((id) => options?.tags.find((x) => x.id === id)?.name ?? 'a removed tag').join(' or ')}`
    } else if (r.kind === 'field') {
      const info = FIELD_INFO[r.field]
      const shown = info.choices?.find((c) => c.value === r.value)?.label ?? r.value
      t = r.op === 'isNull' || r.op === 'notNull' || info.kind === 'boolean'
        ? `${info.label.toLowerCase()} ${opLabel(info.kind, r.op)}${info.kind === 'boolean' ? ` ${r.value === 'true' ? 'yes' : 'no'}` : ''}`
        : `${info.label.toLowerCase()} ${opLabel(info.kind, r.op)} ${shown}`
    } else if (r.kind === 'stage') t = `stage is ${r.values.map((v) => STAGES.find((s) => s.id === v)?.label ?? v).join(' or ')}`
    else t = `${KIND_LABEL[r.kind].toLowerCase()} ${r.values.join(' or ')}`
    return r.exclude ? `not (${t})` : t
  })
  const joined = parts.join(d.join === 'all' ? ', and ' : ', or ')
  return d.paidOnly ? `Paid, and ${joined}` : joined.charAt(0).toUpperCase() + joined.slice(1)
}

export function CohortEditor({
  editing,
  initial,
  onDone,
}: {
  /** The bucket id when editing; null for a new cohort. */
  editing: string | null
  initial: Draft
  /** Called with the new cohort's id after creating; with nothing after editing or cancelling. */
  onDone: (createdId?: string) => void
}) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const [d, setD] = useState<Draft>(initial)
  const options = useQuery(trpc.buckets.options.queryOptions())
  const rule = draftRule(d)
  const deferred = useDeferredValue(rule)
  const preview = useQuery({ ...trpc.buckets.preview.queryOptions({ rule: deferred ?? { all: [] } }), enabled: deferred !== null })
  const done = (createdId?: string) => {
    void qc.invalidateQueries()
    onDone(createdId)
  }
  const create = useMutation(trpc.buckets.create.mutationOptions({ onSuccess: (r) => done(r.id) }))
  const update = useMutation(trpc.buckets.update.mutationOptions({ onSuccess: () => done() }))
  const saving = create.isPending || update.isPending
  const error = create.error ?? update.error

  const setRow = (i: number, r: Row) => setD({ ...d, rows: d.rows.map((x, j) => (j === i ? r : x)) })
  const canSave = d.name.trim() !== '' && rule !== null && !saving

  const save = () => {
    if (!rule) return
    const bucket = {
      name: d.name.trim(),
      description: d.description.trim() || describeDraft(d, options.data),
      audience: d.audience,
      rule,
    }
    if (editing) update.mutate({ id: editing, bucket })
    else create.mutate(bucket)
  }

  return (
    <section className="panel cohort-editor" aria-label={editing ? 'Edit cohort' : 'New cohort'}>
      <div className="panel-h">
        <h3>{editing ? 'Edit cohort' : 'New cohort'}</h3>
      </div>
      <div className="edit-form">
        <label>
          <span>Name</span>
          <input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} placeholder="Tim Ferriss buyers" autoFocus />
        </label>
        <label>
          <span>Description (optional; the conditions in words if left empty)</span>
          <input value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} />
        </label>

        <label>
          <span>Opens as</span>
          <select value={d.audience} onChange={(e) => setD({ ...d, audience: e.target.value as Draft['audience'] })}>
            <option value="patients">Users (people who bought or trained)</option>
            <option value="prospects">Leads (waitlist and sign-ups who have not bought)</option>
          </select>
        </label>

        <div className="cohort-join">
          People who match{' '}
          <select value={d.join} onChange={(e) => setD({ ...d, join: e.target.value as 'all' | 'any' })}>
            <option value="all">all of</option>
            <option value="any">any of</option>
          </select>{' '}
          these:
        </div>

        {d.rows.map((r, i) => (
          <RowEditor
            key={i}
            row={r}
            options={options.data}
            onChange={(x) => setRow(i, x)}
            onRemove={d.rows.length > 1 ? () => setD({ ...d, rows: d.rows.filter((_, j) => j !== i) }) : undefined}
          />
        ))}

        <div className="cohort-add">
          Add a condition:{' '}
          {(Object.keys(KIND_LABEL) as Row['kind'][]).map((k) => (
            <button key={k} type="button" className="btn ghost" onClick={() => setD({ ...d, rows: [...d.rows, blank(k)] })}>
              + {KIND_LABEL[k]}
            </button>
          ))}
        </div>

        <label className="cohort-check">
          <input type="checkbox" checked={d.paidOnly} onChange={(e) => setD({ ...d, paidOnly: e.target.checked })} />
          <span>Only people who paid (leaves out waitlist sign-ups who never bought)</span>
        </label>

        <div className="cohort-preview" aria-live="polite">
          {rule === null ? (
            <span className="muted">Fill in a condition to see who it selects.</span>
          ) : preview.error ? (
            <ErrorNote error={preview.error} />
          ) : !preview.data ? (
            <span className="muted">Counting…</span>
          ) : (
            <>
              <b className="num">{preview.data.count.toLocaleString()}</b> {preview.data.count === 1 ? 'person' : 'people'}
              {preview.data.sample.length > 0 && (
                <span className="muted">
                  {' '}
                  · {preview.data.sample.map((s) => s.name).join(', ')}
                  {preview.data.count > preview.data.sample.length ? ', …' : ''}
                </span>
              )}
              <div className="muted cohort-words">{describeDraft(d, options.data)}</div>
            </>
          )}
        </div>

        {error && <ErrorNote error={error} />}
        <div className="edit-actions">
          <button type="button" className="btn primary" aria-disabled={!canSave} onClick={save}>
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Save cohort'}
          </button>
          <button type="button" className="btn ghost" onClick={() => onDone()}>
            Cancel
          </button>
        </div>
      </div>
    </section>
  )
}

function RowEditor({
  row,
  options,
  onChange,
  onRemove,
}: {
  row: Row
  options: CohortOptions | undefined
  onChange: (r: Row) => void
  onRemove?: () => void
}) {
  return (
    <fieldset className="cohort-row">
      <legend>
        {KIND_LABEL[row.kind]}
        <label className="cohort-exclude">
          <input type="checkbox" checked={row.exclude} onChange={(e) => onChange({ ...row, exclude: e.target.checked })} /> not
        </label>
        {onRemove && (
          <button type="button" className="btn ghost cohort-remove" onClick={onRemove} aria-label="Remove this condition">
            Remove
          </button>
        )}
      </legend>

      {row.kind === 'order' && (
        <div className="cohort-fields">
          <div className="cohort-products">
            <span>Product (any of; none ticked means any product)</span>
            {(options?.products ?? []).map((p) => (
              <label key={p.id} className="cohort-check">
                <input
                  type="checkbox"
                  checked={row.products.includes(p.id)}
                  onChange={(e) =>
                    onChange({ ...row, products: e.target.checked ? [...row.products, p.id] : row.products.filter((x) => x !== p.id) })
                  }
                />
                <span>
                  {p.name} <span className="muted">· {p.orders} {p.orders === 1 ? 'order' : 'orders'}, {p.firstAt.slice(0, 10)} to {p.lastAt.slice(0, 10)}</span>
                </span>
              </label>
            ))}
          </div>
          <label>
            <span>Paid at least ($)</span>
            <input inputMode="decimal" value={row.min} onChange={(e) => onChange({ ...row, min: e.target.value })} />
          </label>
          <label>
            <span>Paid at most ($)</span>
            <input inputMode="decimal" value={row.max} onChange={(e) => onChange({ ...row, max: e.target.value })} />
          </label>
          <label>
            <span>Paid on or after</span>
            <input type="date" value={row.from} onChange={(e) => onChange({ ...row, from: e.target.value })} />
          </label>
          <label>
            <span>Paid before</span>
            <input type="date" value={row.before} onChange={(e) => onChange({ ...row, before: e.target.value })} />
          </label>
          <label>
            <span>Discount code</span>
            <input list="cohort-codes" value={row.code} onChange={(e) => onChange({ ...row, code: e.target.value })} />
            <datalist id="cohort-codes">
              {(options?.codes ?? []).map((c) => (
                <option key={c.value} value={c.value} />
              ))}
            </datalist>
          </label>
        </div>
      )}

      {row.kind === 'tag' && (
        <div className="cohort-products">
          <span>Any of these tags</span>
          <div className="person-tags">
            {row.values.map((id) => (
              <span key={id} className="tagchip">
                {options?.tags.find((x) => x.id === id)?.name ?? 'a removed tag'}
                <button type="button" aria-label="Remove" onClick={() => onChange({ ...row, values: row.values.filter((v) => v !== id) })}>
                  ×
                </button>
              </span>
            ))}
            <TagPicker
              label="Choose a tag"
              allowCreate={false}
              exclude={row.values}
              onPick={(t) => onChange({ ...row, values: [...row.values, t.id] })}
            />
          </div>
        </div>
      )}

      {(row.kind === 'signupSource' || row.kind === 'signupPromo' || row.kind === 'stage') && (
        <div className="cohort-products">
          <span>Any of</span>
          {(row.kind === 'stage'
            ? STAGES.map((s) => ({ value: s.id as string, label: s.label, count: null as number | null }))
            : (row.kind === 'signupSource' ? options?.signupSources : options?.signupPromos)?.map((o) => ({ value: o.value, label: o.value, count: o.count as number | null })) ?? []
          ).map((o) => (
            <label key={o.value} className="cohort-check">
              <input
                type="checkbox"
                checked={row.values.includes(o.value)}
                onChange={(e) =>
                  onChange({ ...row, values: e.target.checked ? [...row.values, o.value] : row.values.filter((x) => x !== o.value) })
                }
              />
              <span>
                {o.label}
                {o.count != null && <span className="muted"> · {o.count}</span>}
              </span>
            </label>
          ))}
        </div>
      )}

      {row.kind === 'field' && <FieldRow row={row} onChange={onChange} />}

      {row.kind === 'sessions' && (
        <div className="cohort-fields">
          <label>
            <span>Comparison</span>
            <select value={row.op} onChange={(e) => onChange({ ...row, op: e.target.value as 'gte' | 'lte' })}>
              <option value="gte">at least</option>
              <option value="lte">at most</option>
            </select>
          </label>
          <label>
            <span>Sessions</span>
            <input inputMode="numeric" value={row.n} onChange={(e) => onChange({ ...row, n: e.target.value })} />
          </label>
        </div>
      )}
    </fieldset>
  )
}

function FieldRow({ row, onChange }: { row: Extract<Row, { kind: 'field' }>; onChange: (r: Row) => void }) {
  const info = FIELD_INFO[row.field]
  const needsValue = row.op !== 'isNull' && row.op !== 'notNull'
  const setField = (field: BucketField) => {
    const kind = FIELD_INFO[field].kind
    const first = FIELD_INFO[field].choices?.[0]?.value
    onChange({
      ...row,
      field,
      op: OPS_FOR[kind][0]!.op,
      value: kind === 'boolean' ? 'true' : kind === 'choice' ? (first ?? '') : '',
    })
  }
  return (
    <div className="cohort-fields">
      <label>
        <span>Field</span>
        <select value={row.field} onChange={(e) => setField(e.target.value as BucketField)}>
          {BUCKET_FIELDS.map((f) => (
            <option key={f} value={f}>
              {FIELD_INFO[f].label}
            </option>
          ))}
        </select>
      </label>
      {info.kind !== 'boolean' && (
        <label>
          <span>Comparison</span>
          <select value={row.op} onChange={(e) => onChange({ ...row, op: e.target.value as FieldOp })}>
            {OPS_FOR[info.kind].map((o) => (
              <option key={o.op} value={o.op}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {needsValue && (
        <label>
          <span>{info.kind === 'boolean' ? 'Is' : 'Value'}{info.hint ? ` (${info.hint})` : ''}</span>
          {info.kind === 'boolean' ? (
            <select value={row.value} onChange={(e) => onChange({ ...row, value: e.target.value })}>
              <option value="true">yes</option>
              <option value="false">no</option>
            </select>
          ) : info.kind === 'choice' ? (
            <select value={row.value} onChange={(e) => onChange({ ...row, value: e.target.value })}>
              {info.choices!.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              inputMode={info.kind === 'number' ? 'decimal' : 'text'}
              value={row.value}
              onChange={(e) => onChange({ ...row, value: e.target.value })}
            />
          )}
        </label>
      )}
    </div>
  )
}
